import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Form, Spinner } from 'react-bootstrap';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { BiArrowBack } from 'react-icons/bi';
import { parivartanAPI } from '../../../services/parivartanAPI';
import useDocumentTitle from '../../../hooks/useDocumentTitle';
import { useAltF1TestFill } from '../../../hooks/useAltF1TestFill';
import { dummyParivartanCustomRoleForm } from '../../../utils/testFormDummies';
import { clearWorkspaceAccess } from '../../../hooks/useWorkspaceAccess';
import { rolesListPath } from './settingsPaths';
import '../tender/CustomerMaster.scss';
import './UserFormPage.scss';
import './RoleFormPage.scss';

const ADMIN_FLOOR = ['users.view', 'users.update', 'roles.view', 'roles.update'];

const EMPTY = { key: '', name: '', permissions: [], isActive: true, isSystem: false };

const monogram = (name) => {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return 'New';
  if (parts.length > 1) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  return parts[0].slice(0, 2).toUpperCase();
};

const Field = ({ label, wide, children }) => (
  <Form.Group className={`cust-field${wide ? ` cust-field--${wide}` : ''}`}>
    <Form.Label>{label}</Form.Label>
    {children}
  </Form.Group>
);

const RoleFormPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { roleKey } = useParams();
  const listPath = rolesListPath(location.pathname);
  const isCreate = location.pathname.endsWith('/new');
  const [form, setForm] = useState(EMPTY);
  const [areas, setAreas] = useState([]);
  const [actions, setActions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useDocumentTitle(isCreate ? 'New role' : (form.name || 'Edit role'));

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const response = await parivartanAPI.listRolesCatalog();
        if (cancelled) return;
        const catalog = response.data?.data || {};
        setAreas(catalog.areas || []);
        setActions(catalog.actions || []);
        if (!isCreate) {
          const row = (catalog.roles || []).find((role) => role.role === roleKey);
          if (!row) {
            setError('That role was not found.');
          } else {
            setForm({
              key: row.isSystem ? row.role : (row.key || ''),
              name: row.name || row.role,
              permissions: row.grants || row.permissions || [],
              isActive: row.isActive !== false,
              isSystem: Boolean(row.isSystem),
              systemRole: row.isSystem ? row.role : ''
            });
          }
        }
      } catch (err) {
        if (!cancelled) setError(err.response?.data?.message || 'Unable to open this role');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [isCreate, roleKey]);

  useAltF1TestFill(isCreate && !loading, () => {
    setForm((current) => ({
      ...current,
      ...dummyParivartanCustomRoleForm(areas.flatMap((area) => area.actions.map((action) => `${area.key}.${action}`))),
      isActive: true,
      isSystem: false
    }));
  });

  const floorLocked = form.systemRole === 'PLATFORM ADMINISTRATOR';
  const groups = useMemo(() => {
    const names = [];
    areas.forEach((area) => {
      if (!names.includes(area.group)) names.push(area.group);
    });
    return names;
  }, [areas]);

  const keyFor = (area, action) => `${area}.${action}`;

  const togglePerm = (area, action) => {
    const permission = keyFor(area.key, action);
    if (floorLocked && ADMIN_FLOOR.includes(permission)) return;
    setForm((current) => {
      const next = new Set(current.permissions);
      const turningOn = !next.has(permission);
      if (turningOn) {
        next.add(permission);
        if (action !== 'view' && area.actions.includes('view')) next.add(keyFor(area.key, 'view'));
      } else {
        next.delete(permission);
        if (action === 'view') {
          area.actions.forEach((item) => {
            const key = keyFor(area.key, item);
            if (!(floorLocked && ADMIN_FLOOR.includes(key))) next.delete(key);
          });
        }
      }
      return { ...current, permissions: [...next] };
    });
  };

  const setRow = (area, selected) => {
    setForm((current) => {
      const next = new Set(current.permissions);
      area.actions.forEach((action) => {
        const permission = keyFor(area.key, action);
        if (floorLocked && ADMIN_FLOOR.includes(permission)) {
          next.add(permission);
          return;
        }
        if (selected) next.add(permission);
        else next.delete(permission);
      });
      return { ...current, permissions: [...next] };
    });
  };

  const save = async (event) => {
    event.preventDefault();
    setError('');
    if (!form.name.trim()) {
      setError('Enter a display name.');
      return;
    }
    if (!form.isSystem && !form.key.trim()) {
      setError('Enter a role key.');
      return;
    }
    setSaving(true);
    try {
      const payload = form.isSystem
        ? { systemRole: form.systemRole, permissions: form.permissions, isActive: form.isActive }
        : { key: form.key, name: form.name.trim(), permissions: form.permissions, isActive: form.isActive };
      await parivartanAPI.upsertRoleTemplate(payload);
      clearWorkspaceAccess();
      navigate(listPath);
    } catch (err) {
      setError(err.response?.data?.message || 'Unable to save this role');
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="cust-page cust-page--loading">
        <Spinner animation="border" size="sm" />
      </div>
    );
  }

  const mark = monogram(form.name);
  const heading = isCreate ? 'New role' : (form.name || 'Edit role');
  const selectedCount = form.permissions.length;

  const renderMatrix = (group, index) => {
    const rows = areas.filter((area) => area.group === group);
    if (!rows.length) return null;
    return (
      <section className="cust-section" key={group}>
        <header className="cust-section__head">
          <span>{String(index).padStart(2, '0')}</span>
          <div>
            <h2>{group}</h2>
            <p>Tick the actions this role may take. Edit is the save action. Approve stays separate from editing.</p>
          </div>
        </header>
        <div className="role-matrix">
          <table>
            <thead>
              <tr>
                <th>Area</th>
                {actions.map((action) => <th key={action.key}>{action.label}</th>)}
                <th>Row</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((area) => {
                const available = area.actions.length;
                const selected = area.actions.filter((action) => form.permissions.includes(keyFor(area.key, action))).length;
                return (
                  <tr key={area.key}>
                    <th>{area.label}</th>
                    {actions.map((action) => {
                      const offered = area.actions.includes(action.key);
                      const permission = keyFor(area.key, action.key);
                      const locked = floorLocked && ADMIN_FLOOR.includes(permission);
                      const checked = offered && (form.permissions.includes(permission) || locked);
                      if (!offered) return <td key={action.key} className="is-empty">—</td>;
                      return (
                        <td key={action.key}>
                          <input
                            type="checkbox"
                            aria-label={`${area.label} ${action.label}`}
                            checked={checked}
                            disabled={locked}
                            onChange={() => togglePerm(area, action.key)}
                          />
                        </td>
                      );
                    })}
                    <td>
                      <button type="button" className="role-matrix__row" onClick={() => setRow(area, selected < available)}>
                        {selected === available ? 'Clear' : 'All'}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>
    );
  };

  return (
    <div className="cust-page user-form role-form">
      <form className="cust-record" onSubmit={save}>
        <header className="cust-mast">
          <div className="cust-mast__identity">
            <button type="button" className="cust-page__back" onClick={() => navigate(listPath)}>
              <BiArrowBack />
              Roles
            </button>
            <div className="cust-mast__row">
              <span className={`cust-mark${mark.length > 2 ? ' cust-mark--wide' : ''}`} aria-hidden>{mark}</span>
              <div className="cust-mast__copy">
                <p className="cust-mast__eyebrow">
                  {form.isSystem ? 'Built-in role' : (isCreate ? 'Role directory' : 'Custom role')}
                </p>
                <h1>{heading}</h1>
                <div className="cust-mast__meta">
                  <span className="cust-chip">{form.key || 'Key on save'}</span>
                  <span className={`cust-status cust-status--${form.isActive ? 'active' : 'inactive'}`}>
                    {form.isActive ? 'Active' : 'Inactive'}
                  </span>
                  <span className="cust-chip">{selectedCount} {selectedCount === 1 ? 'permission' : 'permissions'}</span>
                </div>
              </div>
            </div>
          </div>
          <div className="cust-mast__actions">
            <Button size="sm" variant="outline-primary" type="button" onClick={() => navigate(listPath)} disabled={saving}>
              Cancel
            </Button>
            <Button
              size="sm"
              variant="primary"
              type="submit"
              disabled={saving || !form.name.trim() || (!form.isSystem && !form.key.trim())}
            >
              {saving ? 'Saving…' : isCreate ? 'Create role' : 'Save changes'}
            </Button>
          </div>
        </header>

        {error ? <Alert variant="danger" className="cust-record__alert">{error}</Alert> : null}

        <div className="cust-record__body">
          <div className="cust-record__main">
            <section className="cust-section">
              <header className="cust-section__head">
                <span>01</span>
                <div>
                  <h2>Identity</h2>
                  <p>
                    {form.isSystem
                      ? 'The key and name are part of the product. Permission changes apply to this company.'
                      : 'The key is stored on each person. The display name is what people see when a role is assigned.'}
                  </p>
                </div>
              </header>
              <div className="cust-fields">
                <Field label="Role key" wide={6}>
                  <Form.Control
                    placeholder="e.g. eligibility_editor"
                    value={form.key}
                    disabled={form.isSystem || !isCreate}
                    onChange={(event) => setForm({
                      ...form,
                      key: event.target.value.trim().toLowerCase().replace(/\s+/g, '_')
                    })}
                  />
                </Field>
                <Field label="Display name" wide={6}>
                  <Form.Control
                    placeholder="Shown when assigning to users"
                    value={form.name}
                    disabled={form.isSystem}
                    required
                    onChange={(event) => setForm({ ...form, name: event.target.value })}
                  />
                </Field>
              </div>
            </section>

            {groups.map((group, index) => renderMatrix(group, index + 2))}
          </div>

          <aside className="cust-rail">
            <div className="cust-rail__card">
              <p className="cust-rail__label">{form.isSystem ? 'Built-in role' : (isCreate ? 'New role' : 'This role')}</p>
              <h2>{form.name.trim() || 'Unnamed role'}</h2>
              <p className="cust-rail__place">{form.key || 'A key is entered on the left'}</p>
              <div className="user-form__switch mt-3">
                <div>
                  <div className="fw-semibold small">Active role</div>
                  <div className="text-muted small">Inactive roles stay on file and are hidden when a user is created or edited.</div>
                </div>
                <Form.Check
                  type="switch"
                  id="role-active-switch"
                  checked={form.isActive}
                  onChange={(event) => setForm({ ...form, isActive: event.target.checked })}
                  label=""
                />
              </div>
              <p className="user-form__hint">{selectedCount} permissions in this bundle.</p>
            </div>
          </aside>
        </div>
      </form>
    </div>
  );
};

export default RoleFormPage;
